'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Accordion from '@/components/ui/Accordion';
import Footer from '@/components/common/Footer';
import StickyCTA from '@/components/ui/StickyCTA';

export default function ExecutionCoverageLander() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'What can I get done with 40 hours per month?',
      content: 'Most clients run 2-3 active channels consistently. Example: Paid ads (15h), email campaigns (10h), social media (10h), reporting & strategy (5h). Or one big project (photoshoot, funnel build) plus ongoing maintenance.'
    },
    {
      id: 'faq-2',
      title: 'What if I need more than 40 hours?',
      content: 'You can add burst capacity at $150/hour or upgrade to Growth Partner for higher monthly capacity and priority scheduling.'
    },
    {
      id: 'faq-3',
      title: 'Do unused hours roll over?',
      content: 'No, but we\'re flexible. If you have a light month, we can bank up to 10 hours for the following month with advance notice.'
    },
    {
      id: 'faq-4',
      title: 'How fast can we start?',
      content: 'We can kick off within 48 hours of payment. Week 1 is onboarding and planning. Week 2 we start executing.'
    }
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <AppImage 
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={180}
              height={68}
              className="cursor-pointer"
            />
          </Link>
          <Link href="/cart">
            <Button variant="primary" size="md" className="border-glow">
              Get Started - $5,000/mo
            </Button>
          </Link>
        </div>
      </nav>

      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">Execution Coverage</p>
              <p className="text-body-sm text-muted-foreground">$5,000/mo • Up to 40 hours</p>
            </div>
            <Link href="/cart">
              <Button variant="primary" size="lg" className="border-glow">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}

      <StickyCTA
        title="Execution Coverage"
        subtitle="$5,000/mo • Up to 40 hours"
        ctaText="Get Started"
        ctaHref="/cart"
        showAfterScroll={800}
      />

      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <Badge variant="primary" className="mb-6">Most Popular Plan</Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                40 hours of execution
                <br />
                <span className="text-primary">every single month</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
                Dedicated account lead. 2-3 active channels. Weekly syncs. No hiring, no managing, no overhead.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Start Execution Coverage - $5,000/mo
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                Cancel anytime with 14 days notice.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="reveal p-8 border border-primary bg-surface-1">
                <h3 className="text-h3 mb-6">This is for you if:</h3>
                <ul className="space-y-4 text-body-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You need consistent execution across 2-3 channels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're tired of managing freelancers or junior teams</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want predictable pricing without hourly billing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You have a clear strategy and need someone to execute it</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're spending $5k-$15k/mo on marketing and need better ROI</span>
                  </li>
                </ul>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <h3 className="text-h3 mb-6">This is NOT for you if:</h3>
                <ul className="space-y-4 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You need more than 40 hours/month (upgrade to Growth Partner)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You want to learn and execute yourself (try AutoPilot)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You need strategy first (start with Strategy Sprint)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're looking for a one-time project (not ongoing execution)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                What's Included
              </span>
              <h2 className="text-h2 mb-4">Everything You Need</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Up to 40 Hours Per Month</h4>
                <p className="text-body-sm text-muted-foreground">
                  Allocated across Always-On, Growth, and Projects based on your priorities.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Dedicated Account Lead</h4>
                <p className="text-body-sm text-muted-foreground">
                  One senior strategist who knows your business and owns your results.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ 2-3 Active Channels</h4>
                <p className="text-body-sm text-muted-foreground">
                  Paid ads, email, social, SEO, content—whatever drives your business.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Weekly Sync Calls</h4>
                <p className="text-body-sm text-muted-foreground">
                  30-minute check-ins to review progress, adjust priorities, and plan ahead.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Monthly Reporting</h4>
                <p className="text-body-sm text-muted-foreground">
                  Clear breakdown of hours spent, results achieved, and next month's plan.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Slack/Email Support</h4>
                <p className="text-body-sm text-muted-foreground">
                  Async communication for quick questions, approvals, and updates.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Timeline
              </span>
              <h2 className="text-h2 mb-4">First 90 Days</h2>
            </div>
            <div className="space-y-8">
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">30</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 1-30: Onboarding & Foundation</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Audit current marketing, set up tracking, finalize strategy, and start execution on 1-2 channels.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">60</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 31-60: Optimization & Expansion</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Optimize existing channels, add 3rd channel if capacity allows, run first A/B tests.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">90</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 61-90: Scale & Refine</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Full execution across all channels, quarterly review, and plan for next 90 days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Results
              </span>
              <h2 className="text-h2 mb-4">What Clients Achieve</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We went from spending $8k/mo on ads with no clear owner to $12k/mo with 3x better ROAS. Execution Coverage gave us consistency."
                </p>
                <p className="text-body-sm font-semibold">— Tom R., DTC Brand</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "Best decision we made. We tried hiring in-house and it was a nightmare. This is like having a senior marketer without the $120k salary."
                </p>
                <p className="text-body-sm font-semibold">— Lisa M., SaaS Startup</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We've been on Execution Coverage for 18 months. It's the most predictable, reliable marketing investment we've ever made."
                </p>
                <p className="text-body-sm font-semibold">— David K., B2B Services</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions?
              </span>
              <h2 className="text-h2 mb-4">Common Questions</h2>
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Start executing in 48 hours.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                $5,000/mo. Up to 40 hours. Cancel anytime with 14 days notice.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Get Started Now
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}